import EC from "elliptic"
import { SHA3 } from "sha3"
import { env } from "../config/env"

const ec = new EC.ec("p256")

/**
 * Derive a deterministic seed from password using PBKDF2
 */
async function deriveSeedFromPassword(password: string, username: string): Promise<Uint8Array> {
  const encoder = new TextEncoder()
  const passwordBuffer = encoder.encode(password)
  const saltBuffer = encoder.encode(env.CRYPTO_SALT + username)

  const seed = await crypto.subtle.importKey("raw", passwordBuffer, { name: "PBKDF2" }, false, ["deriveBits"])

  // Derive for the private key
  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      salt: saltBuffer,
      iterations: env.PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    seed,
    256, // 32 bytes
  )

  return new Uint8Array(derivedBits)
}

/**
 * Generate key pair from password and username so it is more unique and prone from rainbow attack (ha gay)
 */
export async function generateKeyPairFromPassword(password: string, username: string) {
  const seed = await deriveSeedFromPassword(password, username)
  const keyPair = ec.keyFromPrivate(Array.from(seed))
  const publicKey = keyPair.getPublic()

  return {
    privateKey: keyPair.getPrivate("hex").padStart(64, "0"),
    publicKeyX: publicKey.getX().toString("hex").padStart(64, "0"),
    publicKeyY: publicKey.getY().toString("hex").padStart(64, "0"),
  }
}

/**
 * Derive public key from private key
 */
export function derivePublicKeyFromPrivate(privateKeyHex: string) {
  const keyPair = ec.keyFromPrivate(privateKeyHex, "hex")
  const publicKey = keyPair.getPublic()

  return {
    publicKeyX: publicKey.getX().toString("hex").padStart(64, "0"),
    publicKeyY: publicKey.getY().toString("hex").padStart(64, "0"),
  }
}

/**
 * Sign a message
 */
export function signMessage(messageHash: string, privateKeyHex: string) {
  const keyPair = ec.keyFromPrivate(privateKeyHex, "hex")
  const signature = keyPair.sign(messageHash, "hex", { canonical: true })

  return {
    r: signature.r.toString("hex").padStart(64, "0"),
    s: signature.s.toString("hex").padStart(64, "0"),
  }
}

/**
 * Verify a message signature
 */
export function verifySignature(
  messageHash: string,
  signature: { r: string; s: string },
  publicKeyX: string,
  publicKeyY: string,
) {
  try {
    const publicKey = ec.keyFromPublic({ x: publicKeyX, y: publicKeyY }, "hex")
    return publicKey.verify(messageHash, { r: signature.r, s: signature.s })
  } catch {
    return false
  }
}

/**
 * Hash message (SHA3-256)
 */
export function hashMessage(
  messageContent: string,
  senderUsername: string,
  receiverUsername: string,
  timestamp: string,
): string {
  const data = `${messageContent}:${senderUsername}:${receiverUsername}:${timestamp}`
  const hash = new SHA3(256)
  hash.update(data)
  return hash.digest("hex")
}

/**
 * Convert hex string to Uint8Array
 */
function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16)
  }
  return bytes
}

/**
 * Convert Uint8Array to hex string
 */
function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
}

/**
 * Derive encryption key using PBKDF2
 */
async function deriveKey(sharedSecret: Uint8Array, salt: string): Promise<CryptoKey> {
  const saltBuffer = new TextEncoder().encode(salt)

  const secretBuffer = new Uint8Array(sharedSecret)
  const baseKey = await crypto.subtle.importKey("raw", secretBuffer, { name: "PBKDF2" }, false, [
    "deriveBits",
    "deriveKey",
  ])

  return await crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: saltBuffer,
      iterations: env.PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  )
}

/**
 * Encrypt message
 */
export async function encryptMessage(
  message: string,
  recipientPublicKeyX: string,
  recipientPublicKeyY: string,
  senderPrivateKeyHex: string,
  senderPublicKeyX: string,
  senderPublicKeyY: string,
) {
  const senderKeyPair = ec.keyFromPrivate(senderPrivateKeyHex, "hex")

  const recipientPublicKey = ec.keyFromPublic({ x: recipientPublicKeyX, y: recipientPublicKeyY }, "hex")

  // Derive shared secret using sender's private key and recipient's public key
  const sharedSecret = senderKeyPair.derive(recipientPublicKey.getPublic())
  const sharedSecretHex = sharedSecret.toString("hex")
  const sharedSecretBuffer = hexToBytes(sharedSecretHex)

  // Derive encryption key
  const encryptionKey = await deriveKey(sharedSecretBuffer, "encryption")

  // Generate random IV (12 bytes for GCM)
  const iv = crypto.getRandomValues(new Uint8Array(12))

  // Encrypt message
  const messageBuffer = new TextEncoder().encode(message)
  const encryptedBuffer = await crypto.subtle.encrypt(
    {
      name: "AES-GCM",
      iv: iv,
    },
    encryptionKey,
    messageBuffer,
  )

  // Contains ciphertext and auth tag
  // AES-GCM automatically append 16-byte auth tag
  const encryptedArray = new Uint8Array(encryptedBuffer)
  const ciphertext = encryptedArray.slice(0, -16)
  const authTag = encryptedArray.slice(-16)

  return {
    ciphertext: bytesToHex(ciphertext),
    ephemeralPublicKey: `${senderPublicKeyX.padStart(64, "0")}:${senderPublicKeyY.padStart(64, "0")}`,
    iv: bytesToHex(iv),
    authTag: bytesToHex(authTag),
  }
}

/**
 * Decrypt message using receiver private key
 */
export async function decryptMessage(
  ciphertext: string,
  senderPublicKeyStr: string,
  iv: string,
  authTag: string,
  recipientPrivateKeyHex: string,
) {
  try {
    const [senderX, senderY] = senderPublicKeyStr.split(":")
    const senderPublicKey = ec.keyFromPublic({ x: senderX, y: senderY }, "hex")

    const recipientPrivateKey = ec.keyFromPrivate(recipientPrivateKeyHex, "hex")

    // Derive shared secret using recipient's private key and sender's public key
    const sharedSecret = recipientPrivateKey.derive(senderPublicKey.getPublic())
    const sharedSecretHex = sharedSecret.toString("hex")
    const sharedSecretBuffer = hexToBytes(sharedSecretHex)

    // Derive decryption key
    const decryptionKey = await deriveKey(sharedSecretBuffer, "encryption")

    const ivBuffer = hexToBytes(iv)
    const ciphertextBuffer = hexToBytes(ciphertext)
    const authTagBuffer = hexToBytes(authTag)

    // Combine ciphertext and auth tag
    const combined = new Uint8Array(ciphertextBuffer.length + authTagBuffer.length)
    combined.set(ciphertextBuffer)
    combined.set(authTagBuffer, ciphertextBuffer.length)

    // Decrypt message
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: new Uint8Array(ivBuffer),
      },
      decryptionKey,
      new Uint8Array(combined),
    )

    return new TextDecoder().decode(decryptedBuffer)
  } catch (error) {
    console.error("Decryption error:", error)
    throw new Error("Failed to decrypt message")
  }
}
